import cx from 'clsx';
import { GET, MPOST } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { Button } from 'ui/component/Button.tsx';
import { IEdit } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Input } from 'ui/control/Input.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';

export const Integrations = () => {
  const { t } = useTranslation();

  return (
    <main className="view-container">
      <h4 className="mb-2">{t('views.trackingmoreConfig.title')}</h4>
      <TrackingmoreAPIKeySection />
    </main>
  );
};

type TrackingmoreAPIKeyEditProps = {
  onClose: () => void;
  isOpen: boolean;
};

const TrackingmoreAPIKeyEditDialog: React.FC<TrackingmoreAPIKeyEditProps> = ({ onClose, isOpen }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const formRef = React.useRef<HTMLFormElement>(null);
  const saveMutation = useSWRMutation('/system-setting', MPOST);

  const error = React.useMemo(() => {
    if (!saveMutation.error) return null;
    return saveMutation.error;
  }, [saveMutation.error]);

  const onSubmit = async ({ trackingmore_api_key }: { trackingmore_api_key: string }) => {
    if (!(await confirm(t('views.trackingmoreConfig.apikey.confirmation')))) return;
    await saveMutation.trigger({ key: 'trackingmore_api_key', value: trackingmore_api_key });
    onClose();
  };

  const formContext = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: { trackingmore_api_key: '' },
  });
  const { handleSubmit, formState } = formContext;

  return (
    <Modal title={<h3>{t('views.trackingmoreConfig.apikey.title')}</h3>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formContext}>
        <form ref={formRef} className="w-[500px]" noValidate={true} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="flex flex-col gap-6 p-1">
            <Input
              label={t('views.trackingmoreConfig.apikey.form.apikey.label')}
              name={'trackingmore_api_key'}
              placeholder={t('views.trackingmoreConfig.apikey.form.apikey.placeholder')}
              registerOptions={{ required: true }}
              autoFocus={true}
            >
              {formState.errors.trackingmore_api_key?.type === 'required' && <div className="text-red">{t('views.trackingmoreConfig.apikey.form.apikey.required')}</div>}
            </Input>
            <div className="mt-6 flex w-full gap-4">
              <Button onClick={onClose} className="blue-outlined">
                {t('actions.back')}
              </Button>
              <Button type="submit" loading={saveMutation.isMutating} disabled={!formState.isValid} className="blue">
                {t('views.trackingmoreConfig.buttons.save')}
              </Button>
            </div>
            {error && <p className="text-red">{t('views.trackingmoreConfig.errors.generic', { message: error.message || t('views.trackingmoreConfig.errors.unknown') })}</p>}
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

const TrackingmoreAPIKeySection = ({ className }: { className?: string }) => {
  const { t } = useTranslation();

  const currentValuesSwr = useSWR(['/system-setting/trackingmore_api_key'], GET, { refreshInterval: 5_000 });

  const [openEditDialog, setOpenEditDialog] = React.useState<boolean>(false);

  const handleOpenDialog = () => setOpenEditDialog(true);

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    void currentValuesSwr.mutate();
  };

  return (
    <section className={cx('relative bg-white p-8', className)}>
      {openEditDialog && <TrackingmoreAPIKeyEditDialog isOpen={openEditDialog} onClose={handleCloseDialog} />}
      <div className="grid grid-cols-2 gap-12">
        <div className="flex flex-col gap-4">
          <h4>{t('views.trackingmoreConfig.apikey.title')}</h4>
          <p>{t('views.trackingmoreConfig.apikey.description')}</p>
        </div>
        <div className="mt-4 mb-8 flex flex-col gap-4">
          <div className="flex min-w-max flex-col justify-center">
            <span className="control-label">{t('views.trackingmoreConfig.apikey.form.apikey.label')}</span>
            <span className="h-[40px] w-max min-w-full border border-grey-border py-2 pl-2 text-black">{currentValuesSwr.data || '-'}</span>
          </div>
          <Button className="blue" onClick={handleOpenDialog} disabled={currentValuesSwr.isLoading} LeftIcon={IEdit} autoFocus={true}>
            {t('actions.edit')}
          </Button>
        </div>
      </div>
    </section>
  );
};
